import {
  Paragraph, TextRun, Table, TableRow, TableCell,
  WidthType, BorderStyle, ShadingType, AlignmentType,
} from 'docx';

const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NONE, bottom: NONE, left: NONE, right: NONE };
const TABLE_NO_BORDERS = { ...NO_BORDERS, insideH: NONE, insideV: NONE };

const GREEN = '04aa00';
const WHITE = 'FFFFFF';
const SECTION_BG = 'f0f0f0';

const KL_COLOR = {
  KL1: 'ba3b46', KL2: 'ba3b46',
  KL3: 'd0c407',
  KL4: '04aa00', KL5: '04aa00',
};

function getKLColor(kl) {
  return KL_COLOR[kl] || '000000';
}

function renderCellValue(v) {
  if (v === 0 || v === '0' || v === null || v === undefined || v === '') return '';
  return String(v);
}

function greenCell(text, span = 1) {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text: String(text), bold: true, color: WHITE, size: 16 })],
      alignment: AlignmentType.CENTER,
    })],
    columnSpan: span,
    shading: { fill: GREEN, type: ShadingType.SOLID },
    borders: NO_BORDERS,
  });
}

function sectionCell(text, shade = SECTION_BG) {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text, bold: true, size: 16 })],
    })],
    shading: { fill: shade, type: ShadingType.SOLID },
    borders: NO_BORDERS,
  });
}

function sectionKlCell(kl, shade = SECTION_BG) {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text: kl, bold: true, color: kl ? getKLColor(kl) : '000000', size: 16 })],
      alignment: AlignmentType.CENTER,
    })],
    shading: { fill: shade, type: ShadingType.SOLID },
    borders: NO_BORDERS,
  });
}

function yearCell(text, shade = 'auto') {
  return new TableCell({
    children: [new Paragraph({
      children: [new TextRun({ text: String(text), size: 16 })],
      alignment: AlignmentType.CENTER,
    })],
    shading: shade !== 'auto' ? { fill: shade, type: ShadingType.SOLID } : { fill: 'auto', type: ShadingType.CLEAR },
    borders: NO_BORDERS,
  });
}

export function renderTekniikkatauluBlockDocx({ type = 'Rakennetekniikka', ptsContext, children }) {
  if (!ptsContext) return;

  const tableData = {
    'Rakennetekniikka': ptsContext.tekniikkaData,
    'LVI Järjestelmät': ptsContext.lviData,
    'Sähköjärjestelmät': ptsContext.sahkoData,
  }[type] || [];

  const totals = {
    'Rakennetekniikka': ptsContext.tekniikkaYhteensa,
    'LVI Järjestelmät': ptsContext.lviYhteensa,
    'Sähköjärjestelmät': ptsContext.sahkoYhteensa,
  }[type] || [];

  const years = ptsContext.years || Array.from({ length: 11 }, (_, i) => new Date().getFullYear() + i);
  const currentYear = new Date().getFullYear();
  const totalCols = 2 + years.length; // name + KL + years

  const tableRows = [];

  // ── Super-header ────────────────────────────────────────────────────────────
  tableRows.push(new TableRow({
    children: [
      greenCell('', 2),
      new TableCell({
        children: [new Paragraph({
          children: [new TextRun({
            text: `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`,
            bold: true, color: WHITE, size: 14,
          })],
          alignment: AlignmentType.RIGHT,
        })],
        columnSpan: years.length,
        shading: { fill: GREEN, type: ShadingType.SOLID },
        borders: NO_BORDERS,
      }),
    ],
  }));

  // ── Column headers ──────────────────────────────────────────────────────────
  tableRows.push(new TableRow({
    children: [
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: type, bold: true, color: WHITE, size: 16 })] })],
        columnSpan: 2,
        shading: { fill: GREEN, type: ShadingType.SOLID },
        borders: NO_BORDERS,
      }),
      ...years.map(y => greenCell(y)),
    ],
  }));

  // ── Body ─────────────────────────────────────────────────────────────────────
  tableData.forEach(section => {
    const klNums = section.items
      .map(it => parseInt((it.kl || '').replace('KL', '')))
      .filter(n => !isNaN(n));
    const avgKL = klNums.length ? 'KL' + Math.round(klNums.reduce((a, b) => a + b, 0) / klNums.length) : '';

    // Section header row
    tableRows.push(new TableRow({
      children: [
        sectionCell(section.name),
        sectionKlCell(avgKL),
        ...years.map(() => new TableCell({
          children: [new Paragraph({ children: [new TextRun('')] })],
          shading: { fill: SECTION_BG, type: ShadingType.SOLID },
          borders: NO_BORDERS,
        })),
      ],
    }));

    // Item rows
    section.items.forEach(item => {
      const label = (item.label || '').trim();
      if (label.length < 2) return;
      tableRows.push(new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: label, size: 16 })] })],
            borders: NO_BORDERS,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun('')] })],
            borders: NO_BORDERS,
          }),
          ...(item.values || []).map(v => yearCell(renderCellValue(v))),
        ],
      }));
    });
  });

  // ── Totals row ───────────────────────────────────────────────────────────────
  tableRows.push(new TableRow({
    children: [
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: 'YHTEENSÄ', bold: true, color: WHITE, size: 16 })] })],
        columnSpan: 2,
        shading: { fill: GREEN, type: ShadingType.SOLID },
        borders: NO_BORDERS,
      }),
      ...totals.map(sum => new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: renderCellValue(sum), bold: true, color: WHITE, size: 16 })],
          alignment: AlignmentType.CENTER,
        })],
        shading: { fill: GREEN, type: ShadingType.SOLID },
        borders: NO_BORDERS,
      })),
    ],
  }));

  children.push(
    new Table({
      rows: tableRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: TABLE_NO_BORDERS,
    })
  );
  children.push(new Paragraph({ children: [new TextRun('')], spacing: { after: 160 } }));
}
